import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    BadgeCheck,
    Calendar,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    ClipboardList,
    GraduationCap,
    LineChart,
    MessageCircle,
    Shield,
    Sparkles,
    Star,
    Target,
    Users,
    X,
} from "lucide-react";

/**
 * UniThink — Comprehensive Counselling Program Landing Page
 * Tech: React + Tailwind + Framer Motion + Lucide
 * Drop-in component. No backend required.
 */

const cx = (...classes) => classes.filter(Boolean).join(" ");

const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] },
    }),
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2 } },
};

function useScrollSpy(ids = []) {
    const [active, setActive] = useState(ids[0] || "");
    useEffect(() => {
        const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
        if (!els.length) return;

        const obs = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));
                if (visible[0]?.target?.id) setActive(visible[0].target.id);
            },
            { root: null, rootMargin: "-35% 0px -55% 0px", threshold: [0.08, 0.12, 0.2, 0.35] }
        );
        els.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, [ids.join("|")]);

    return active;
}

function Pill({ children, icon: Icon }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-sm text-slate-700 shadow-sm backdrop-blur">
            {Icon ? <Icon className="h-4 w-4 text-slate-700" /> : null}
            {children}
        </span>
    );
}

function SectionTitle({ kicker, title, subtitle }) {
    return (
        <div className="mx-auto max-w-3xl text-center">
            {kicker ? (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/60 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
                    <Sparkles className="h-3.5 w-3.5" />
                    {kicker}
                </div>
            ) : null}
            <h2 className="text-balance text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                {title}
            </h2>
            {subtitle ? (
                <p className="mt-3 text-pretty text-slate-600">{subtitle}</p>
            ) : null}
        </div>
    );
}

function Card({ className, children }) {
    return (
        <div
            className={cx(
                "rounded-2xl border border-slate-200 bg-white shadow-sm",
                "transition hover:shadow-md",
                className
            )}
        >
            {children}
        </div>
    );
}

function IconBadge({ icon: Icon, title, desc }) {
    return (
        <div className="flex gap-3">
            <div className="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
                <Icon className="h-5 w-5 text-slate-900" />
            </div>
            <div>
                <div className="font-medium text-slate-900">{title}</div>
                <div className="text-sm text-slate-600">{desc}</div>
            </div>
        </div>
    );
}

function FAQItem({ q, a, open, onToggle }) {
    return (
        <Card className="overflow-hidden">
            <button
                onClick={onToggle}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
                <span className="font-medium text-slate-900">{q}</span>
                {open ? (
                    <ChevronUp className="h-5 w-5 text-slate-600" />
                ) : (
                    <ChevronDown className="h-5 w-5 text-slate-600" />
                )}
            </button>
            <AnimatePresence initial={false}>
                {open ? (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="border-t border-slate-200"
                    >
                        <div className="px-5 py-4 text-sm leading-relaxed text-slate-600">{a}</div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </Card>
    );
}

function Modal({ open, onClose, title, children }) {
    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <div
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        variants={scaleIn}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                    >
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                            <div className="font-semibold text-slate-900">{title}</div>
                            <button
                                onClick={onClose}
                                className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="px-5 py-5">{children}</div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function Toast({ show, message, onClose }) {
    return (
        <AnimatePresence>
            {show ? (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.25 }}
                    className="fixed bottom-4 left-1/2 z-[60] w-[min(560px,calc(100%-2rem))] -translate-x-1/2"
                >
                    <div className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2 text-white">
                                <CheckCircle2 className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-medium text-slate-900">Done.</div>
                                <div className="text-sm text-slate-600">{message}</div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

export default function UniThinkComprehensiveCounsellingLanding() {
    const sectionIds = useMemo(
        () => ["overview", "included", "sessions", "how", "pricing", "faqs"],
        []
    );
    const active = useScrollSpy(sectionIds);

    const [openModal, setOpenModal] = useState(false);
    const [toast, setToast] = useState(false);
    const [faqOpen, setFaqOpen] = useState(0);

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [pathway, setPathway] = useState("Not sure yet");
    const [notes, setNotes] = useState("");

    const handlePayment = () => {
        if (!name || !phone || !email) {
            alert("Please fill all required details (Name, Phone, Email).");
            return;
        }
        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY,
            amount: 2950000, // ₹25,000 in paise
            currency: "INR",
            name: "UniThink",
            description: "Comprehensive Counselling Program",
            handler: function (response) {
                // Send data to Google Sheets
                fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                    method: "POST",
                    mode: "no-cors",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        studentName: name,
                        mobileNumber: phone,
                        email: email,
                        planName: "Comprehensive Counselling Program",
                        pathway: pathway,
                        notes: notes,
                        paymentStatus: "Paid",
                        paymentId: response.razorpay_payment_id,
                        service: "Comprehensive Counselling Program",
                        amount: 29500
                    }),
                }).finally(() => {
                    alert("Payment Successful! We have received your request.");
                    window.location.href = "/";
                });
            },
            prefill: {
                name: name,
                contact: phone,
            },
            theme: { color: "#0f172a" },
            modal: {
                ondismiss: function () {
                    fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                        method: "POST",
                        mode: "no-cors",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            studentName: name,
                            mobileNumber: phone,
                            email: email,
                            planName: "Comprehensive Counselling Program",
                            pathway: pathway,
                            notes: notes,
                            paymentStatus: "Abandoned",
                            paymentId: "N/A",
                            service: "Comprehensive Counselling Program",
                            amount: 29500
                        }),
                    });
                }
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name,
                    phone: phone,
                    pathway: pathway,
                    notes: notes,
                    payment_status: "Payment Failed",
                    paymentId: response.error.metadata.payment_id || "N/A",
                    service: "Comprehensive Counselling Program",
                    amount: 29500
                }),
            });
        });
        rzp.open();
    };

    const nav = [
        { id: "overview", label: "Overview" },
        { id: "included", label: "What’s Included" },
        { id: "sessions", label: "Session Plan" },
        { id: "how", label: "How it Works" },
        { id: "pricing", label: "Pricing" },
        { id: "faqs", label: "FAQs" },
    ];

    const faqs = [
        {
            q: "Do I need this if I already know what I want?",
            a: "If you have clarity, the program helps validate feasibility, strengthen the plan, and avoid avoidable missteps.",
        },
        {
            q: "How long does the program run?",
            a: "Duration depends on scheduling and your stage. Define and show a clear validity period in your policy.",
        },
        {
            q: "Is university shortlisting included?",
            a: "Yes, shortlist strategy support is included. The depth and final shortlist scope can be clarified at onboarding.",
        },
        {
            q: "Does this include SOP/LOR drafting?",
            a: "Not typically. SOP/LOR guidance is usually part of the applications support package.",
        },
        {
            q: "What if my goals change mid-way?",
            a: "That is common. The program is designed to refine direction as evidence and preferences evolve.",
        },
        {
            q: "Is it suitable for both UG and PG?",
            a: "Yes. The framework works for both UG and PG decision-making.",
        },
        {
            q: "Do you provide psychometric testing?",
            a: "If psychometric assessment is a separate service or included in a different package, clarify the inclusion explicitly on the page.",
        },
        {
            q: "Do you guarantee admissions or scholarships?",
            a: "No. Outcomes depend on institutions and eligibility; UniThink supports strategy and execution.",
        },
    ];

    const onPrimaryCTA = () => setOpenModal(true);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Top glow */}
            <div className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-80 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(15,23,42,0.08)_0%,rgba(15,23,42,0)_70%)]" />

            {/* Header */}
            <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
                    <div className="flex items-center gap-3">

                        <span className="ml-2 hidden items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 sm:inline-flex">
                            <Star className="h-3.5 w-3.5" /> Most Popular
                        </span>
                    </div>

                    <nav className="hidden items-center gap-1 lg:flex">
                        {nav.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => scrollTo(n.id)}
                                className={cx(
                                    "rounded-xl px-3 py-2 text-sm transition",
                                    active === n.id
                                        ? "bg-slate-900 text-white"
                                        : "text-slate-700 hover:bg-slate-100"
                                )}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                scrollTo("pricing");
                            }}
                            className="hidden rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 sm:inline-flex"
                        >
                            View Pricing
                        </button>
                        <button
                            onClick={onPrimaryCTA}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                        >
                            Start Program <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <main>
                <section className="relative">
                    <div className="mx-auto max-w-6xl px-4 pb-10 pt-10 sm:px-6 sm:pb-14 sm:pt-14">
                        <div className="grid items-start gap-10 lg:grid-cols-[1.2fr_0.8fr]">
                            <div>
                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(0)}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm"
                                >
                                    <BadgeCheck className="h-4 w-4" /> Structured guidance across decision stages
                                </motion.div>

                                <motion.h1
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(1)}
                                    className="mt-4 text-balance text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
                                >
                                    Comprehensive Counselling Program
                                </motion.h1>

                                <motion.p
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(2)}
                                    className="mt-4 text-pretty text-slate-600"
                                >
                                    A structured, multi-session counselling program for students and parents who want
                                    clear career direction, goal alignment, and confident decision-making across multiple
                                    stages.
                                </motion.p>

                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(3)}
                                    className="mt-6 flex flex-wrap gap-2"
                                >
                                    <Pill icon={Users}>Six 1:1 counselling sessions</Pill>
                                    <Pill icon={Target}>In-depth profile evaluation</Pill>
                                    <Pill icon={LineChart}>Career mapping & long-term pathway</Pill>
                                    <Pill icon={ClipboardList}>Shortlisting strategy support</Pill>
                                    <Pill icon={MessageCircle}>Ongoing decision guidance</Pill>
                                </motion.div>

                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(4)}
                                    className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center"
                                >
                                    <button
                                        onClick={onPrimaryCTA}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                    >
                                        Start Program <ArrowRight className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => scrollTo("included")}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50"
                                    >
                                        What’s Included
                                        <ChevronDown className="h-5 w-5" />
                                    </button>
                                    <div className="text-sm text-slate-500 sm:ml-2">
                                        Best for structured planning (not one-off advice).
                                    </div>
                                </motion.div>
                            </div>

                            {/* Credibility + quick facts */}
                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(2)}
                                className="lg:pt-3"
                            >
                                <Card className="overflow-hidden">
                                    <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                                        <div className="flex items-center justify-between">
                                            <div className="font-semibold">Why guidance matters</div>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                                                <Shield className="h-3.5 w-3.5" /> Evidence-based
                                            </span>
                                        </div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            A small panel your designer can keep across all UniThink pages.
                                        </p>
                                    </div>
                                    <div className="space-y-4 px-5 py-5">
                                        <IconBadge
                                            icon={GraduationCap}
                                            title="Expanding choices = harder decisions"
                                            desc="Global higher education enrolment reached 264 million; international mobility reported at 6.9 million — signalling complex, global pathways."
                                        />
                                        <IconBadge
                                            icon={Target}
                                            title="Uncertainty is common"
                                            desc="OECD PISA 2022 analysis finds two in five students have no clear career plans; anxiety about readiness is widely reported."
                                        />
                                        <IconBadge
                                            icon={LineChart}
                                            title="Employability alignment matters"
                                            desc="ILO-linked analysis shows youth unemployment rises with education level; highest among graduates in 2022 — better decisions reduce misalignment risk."
                                        />
                                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <div className="text-sm font-semibold text-slate-900">Quick promise</div>
                                            <p className="mt-1 text-sm text-slate-600">
                                                A one-time session helps. A structured program keeps decisions consistent as new
                                                information appears — shortlisting, timelines, and trade-offs included.
                                            </p>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Overview / Problem context */}
                <section id="overview" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="The problem this solves"
                            title="Stop making high-stakes decisions with low-info + high-pressure"
                            subtitle="Career decisions are often made too early, with too little information. This program builds a structured decision framework across clarity → mapping → shortlisting → decision support → next steps."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-3">
                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <Users className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Career uncertainty is real</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            OECD analysis of PISA 2022 finds two in five students across OECD countries have no
                                            clear career plans.
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <Calendar className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Readiness anxiety is common</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            In the same dataset, many students report anxiety: 47% worry they’re not prepared for
                                            life after compulsory education.
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <LineChart className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Better alignment = better outcomes</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            ILO-linked analysis shows youth unemployment rises with education level; it was highest
                                            among graduates in 2022.
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <div className="mt-8 grid gap-6 lg:grid-cols-2">
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Sparkles className="h-4 w-4" /> Why guidance matters
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    Evidence summaries from UNESCO-UNEVOC note tangible benefits associated with comprehensive
                                    guidance and counselling programs, including improved academic achievement and reduced
                                    drop-out rates (evidence cited across multiple country contexts).
                                </p>
                            </Card>
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <ClipboardList className="h-4 w-4" /> What this program does
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    This program provides a structured decision framework across the main stages of career and
                                    admissions thinking. Instead of isolated advice, you receive continuity — so decisions stay
                                    consistent and aligned as new information appears.
                                </p>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Included */}
                <section id="included" className="bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="What is included"
                            title="Six sessions. One clear direction. Zero chaos."
                            subtitle="A structured, sequenced program designed to keep your plan consistent across decision stages."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-3">
                            {[
                                {
                                    icon: Users,
                                    title: "Six 1:1 personalised sessions",
                                    desc: "Structured and sequenced to your stage — not random advice.",
                                },
                                {
                                    icon: Target,
                                    title: "In-depth profile evaluation",
                                    desc: "Academics, interests, constraints, goals — mapped into a realistic plan.",
                                },
                                {
                                    icon: LineChart,
                                    title: "Career mapping + goal alignment",
                                    desc: "Long-term pathway planning that balances aspiration and feasibility.",
                                },
                                {
                                    icon: GraduationCap,
                                    title: "Country + education pathway strategy",
                                    desc: "India and/or abroad — built around timelines and outcomes.",
                                },
                                {
                                    icon: ClipboardList,
                                    title: "Shortlisting strategy support",
                                    desc: "Criteria, fit logic, prioritisation — a defensible approach.",
                                },
                                {
                                    icon: MessageCircle,
                                    title: "Ongoing decision guidance",
                                    desc: "Shortlisting → final decisions → roadmap for the next stage.",
                                },
                            ].map((it, idx) => (
                                <motion.div
                                    key={it.title}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView={fadeUp.show(idx)}
                                    viewport={{ once: true, amount: 0.25 }}
                                >
                                    <Card className="p-6">
                                        <div className="flex items-start gap-3">
                                            <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                                <it.icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-slate-900">{it.title}</div>
                                                <p className="mt-1 text-sm text-slate-600">{it.desc}</p>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                            <button
                                onClick={onPrimaryCTA}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                            >
                                Start Program <ArrowRight className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => scrollTo("sessions")}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50"
                            >
                                See Session Plan <ChevronDown className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* Session structure */}
                <section id="sessions" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Suggested session structure"
                            title="A clean roadmap (so you don’t spiral mid-way)"
                            subtitle="Six themes designed to refine direction as evidence and preferences evolve."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-2">
                            {[
                                {
                                    t: "Session theme 1",
                                    h: "Profile evaluation and baseline clarity",
                                    d: "Understand current stage, constraints, preferences, and the real problem to solve.",
                                },
                                {
                                    t: "Session theme 2",
                                    h: "Career mapping and pathway options",
                                    d: "Explore realistic pathways and how each connects to future outcomes.",
                                },
                                {
                                    t: "Session theme 3",
                                    h: "Country/course strategy (India and/or abroad)",
                                    d: "Build a timeline + strategy based on feasibility, cost, and fit.",
                                },
                                {
                                    t: "Session theme 4",
                                    h: "University shortlist logic and prioritisation",
                                    d: "Create a defensible shortlist using fit criteria (not random rankings).",
                                },
                                {
                                    t: "Session theme 5",
                                    h: "Decision-stage guidance",
                                    d: "Trade-offs, feasibility, and timelines — reduce last-minute confusion.",
                                },
                                {
                                    t: "Session theme 6",
                                    h: "Final plan consolidation + next-step roadmap",
                                    d: "Lock direction, define next steps, and set execution priorities.",
                                },
                            ].map((s, idx) => (
                                <motion.div
                                    key={s.h}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView={fadeUp.show(idx)}
                                    viewport={{ once: true, amount: 0.25 }}
                                >
                                    <Card className="p-6">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div className="text-xs font-semibold text-slate-600">{s.t}</div>
                                                <div className="mt-1 text-lg font-semibold text-slate-900">{s.h}</div>
                                                <p className="mt-2 text-sm text-slate-600">{s.d}</p>
                                            </div>
                                            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl border border-slate-200 bg-white">
                                                <span className="text-sm font-semibold text-slate-900">{idx + 1}</span>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* How it works */}
                <section id="how" className="bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="How it works"
                            title="Simple flow. Serious outcomes."
                            subtitle="Sessions are spaced so you get time to reflect, research, and return with better clarity each time."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-3">
                            {[
                                {
                                    icon: ClipboardList,
                                    title: "Step 1: Enrol + structured intake",
                                    desc: "You enrol and complete a structured intake for background and goals.",
                                },
                                {
                                    icon: Calendar,
                                    title: "Step 2: Schedule across stages",
                                    desc: "Sessions are scheduled across decision stages (recommended spacing for reflection and research).",
                                },
                                {
                                    icon: BadgeCheck,
                                    title: "Step 3: Refine until confident",
                                    desc: "Your plan is refined across sessions until you reach a clear, confident direction.",
                                },
                            ].map((it, idx) => (
                                <motion.div
                                    key={it.title}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView={fadeUp.show(idx)}
                                    viewport={{ once: true, amount: 0.25 }}
                                >
                                    <Card className="p-6">
                                        <div className="flex items-start gap-3">
                                            <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                                <it.icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-slate-900">{it.title}</div>
                                                <p className="mt-1 text-sm text-slate-600">{it.desc}</p>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 grid gap-6 lg:grid-cols-1">
                            <Card className="flex flex-col items-center p-6 text-center">
                                <div className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-900">
                                    <CheckCircle2 className="h-4 w-4" /> Outcomes you can expect
                                </div>
                                <ul className="mt-3 flex flex-col items-start space-y-2 text-left text-sm text-slate-600">
                                    {[
                                        "Clear long-term academic direction instead of “trial and error”",
                                        "Better alignment between aspirations, abilities, and realistic opportunities",
                                        "A defensible shortlisting approach (why this university/course/country)",
                                        "Reduced last-minute decision stress and family confusion",
                                        "A structured roadmap that enables faster execution when you decide to apply",
                                    ].map((x) => (
                                        <li key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>


                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                                <div>
                                    <div className="text-lg font-semibold text-slate-900">Ready to make decisions… consistently?</div>
                                    <div className="mt-1 text-sm text-slate-600">
                                        Get a structured plan that stays stable across decision stages.
                                    </div>
                                </div>
                                <button
                                    onClick={onPrimaryCTA}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Start Program <ArrowRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing */}
                <section id="pricing" className="border-t border-slate-200 bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Price & positioning"
                            title="₹25,000 + 18% GST — structured planning over six sessions"
                            subtitle="Best for students and parents who want ongoing guidance rather than a single conversation."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1fr]">
                            <Card className="p-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                            <Star className="h-4 w-4" /> Most Popular
                                        </div>
                                        <div className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">₹25,000 + 18% GST</div>
                                        <div className="mt-1 text-sm text-slate-600">per package (6 sessions)</div>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                        <div className="text-xs font-semibold text-slate-600">Includes</div>
                                        <div className="mt-1 text-sm font-semibold text-slate-900">6 × 1:1 sessions</div>
                                    </div>
                                </div>

                                <div className="mt-6 space-y-2 text-sm text-slate-600">
                                    {[
                                        "Profile evaluation + clarity",
                                        "Career mapping + pathways",
                                        "Country/course strategy",
                                        "Shortlisting logic + prioritisation",
                                        "Decision-stage guidance",
                                        "Final plan + roadmap",
                                    ].map((x) => (
                                        <div key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={onPrimaryCTA}
                                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Start Program <ArrowRight className="h-5 w-5" />
                                </button>

                                <div className="mt-3 text-center text-xs text-slate-500">
                                    Tip: Add your rescheduling + validity policy below the CTA.
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="text-sm font-semibold text-slate-900">Recommended reading</div>
                                <div className="mt-4 space-y-3">
                                    {[
                                        "Career uncertainty: why it happens and how to reduce it",
                                        "Shortlisting strategy: fit vs ranking vs ROI",
                                        "Common admissions timeline mistakes (India and abroad)",
                                        "Smart study abroad decision- Episode 1",
                                    ].map((x) => (
                                        <div
                                            key={x}
                                            className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 transition hover:bg-slate-50"
                                        >
                                            <div className="font-semibold text-slate-900">{x}</div>
                                            <div className="mt-1 text-xs text-slate-500">Comming soon</div>
                                        </div>
                                    ))}
                                </div>


                            </Card>
                        </div>
                    </div>
                </section>

                {/* FAQs */}
                <section id="faqs" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="FAQs"
                            title="Questions you’ll ask anyway (so we answered them)"
                            subtitle="Clear expectations = smoother counselling."
                        />

                        <div className="mt-10 grid gap-4 lg:grid-cols-2">
                            {faqs.map((f, i) => (
                                <FAQItem
                                    key={f.q}
                                    q={f.q}
                                    a={f.a}
                                    open={faqOpen === i}
                                    onToggle={() => setFaqOpen((p) => (p === i ? -1 : i))}
                                />
                            ))}
                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
                            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                                <div>
                                    <div className="text-lg font-semibold text-slate-900">
                                        Get a structured plan that stays consistent across decision stages.
                                    </div>
                                    <div className="mt-1 text-sm text-slate-600">Start the Comprehensive Counselling Program today.</div>
                                </div>
                                <button
                                    onClick={onPrimaryCTA}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Start Program <ArrowRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>


            </main>

            {/* Modal */}
            <Modal
                open={openModal}
                onClose={() => setOpenModal(false)}
                title="Start Program — Request a Callback"
            >
                <div className="grid gap-4 sm:grid-cols-2">
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Student Name</span>
                        <input
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., Aarav"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Phone Number</span>
                        <input
                            value={phone} onChange={(e) => setPhone(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., +91 XXXXX XXXXX"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Email Address</span>
                        <input
                            type="email"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., student@gmail.com"
                        />
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Preferred pathway</span>
                        <select value={pathway} onChange={(e) => setPathway(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400">
                            <option>Not sure yet</option>
                            <option>India admissions</option>
                            <option>Study abroad</option>
                            <option>Need comparison (India vs abroad)</option>
                        </select>
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Anything we should know?</span>
                        <textarea
                            value={notes} onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="Budget constraints, target intake, course interest, etc."
                        />
                    </label>
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-slate-500">
                        Next step: connect this form to WhatsApp / Razorpay / Google Sheets.
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setOpenModal(false)}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                        >
                            Not now
                        </button>
                        <button
                            onClick={handlePayment}
                            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:translate-y-[-1px]"
                        >
                            Continue <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </Modal>

            <Toast
                show={toast}
                message="We captured your request (demo). Next, connect this to your actual booking/payment flow."
                onClose={() => setToast(false)}
            />
        </div>
    );
}
